import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { Package, ArrowRight, ArrowLeft, Sparkles } from "lucide-react";

type Step = 'create_boxes' | 'add_details' | 'schedule_pickup';

const GuidedStorageOnboarding = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [currentStep, setCurrentStep] = useState<Step>('create_boxes');
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<any>(null);
  
  const boxSlots = parseInt(searchParams.get("box_slots") || "0");
  const [boxNames, setBoxNames] = useState<string[]>(Array(boxSlots).fill(""));
  const [boxCategories, setBoxCategories] = useState<string[]>(Array(boxSlots).fill("Other"));
  const [createdBoxIds, setCreatedBoxIds] = useState<string[]>([]);
  
  const [pickupDate, setPickupDate] = useState<Date>();
  const [timeSlot, setTimeSlot] = useState<string>("");
  const [instructions, setInstructions] = useState<string>("");

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please check your email and click the magic link",
        variant: "destructive"
      });
      return;
    }
    setUser(user);
  };

  const quickFillBoxNames = () => {
    const names = Array.from({ length: boxSlots }, (_, i) => `Box ${i + 1}`);
    setBoxNames(names);
    toast({ title: "Names filled!", description: "You can edit any name" });
  };

  const handleCreateBoxes = async () => {
    const emptyNames = boxNames.filter(name => !name.trim());
    if (emptyNames.length > 0) {
      toast({
        title: "Names required",
        description: "Please name all boxes before continuing",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const boxesToCreate = boxNames.map((name, index) => ({
        user_id: user.id,
        name: name.trim(),
        category: boxCategories[index],
        location_status: 'at_home',
        source: 'direct_storage_purchase',
        box_number: `BOX-${Date.now()}-${index + 1}`
      }));

      const { data: boxes, error: boxError } = await supabase
        .from('boxes')
        .insert(boxesToCreate)
        .select();

      if (boxError) throw boxError;

      const boxIds = boxes.map(b => b.id);
      setCreatedBoxIds(boxIds);

      await supabase.from('profiles').update({
        onboarding_status: 'boxes_created',
        box_slots_used: boxSlots
      }).eq('id', user.id);

      toast({ title: "Boxes created!", description: `${boxSlots} boxes ready` });
      setCurrentStep('add_details');
    } catch (error) {
      console.error('Error creating boxes:', error);
      toast({
        title: "Error",
        description: "Failed to create boxes",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSchedulePickup = async () => {
    if (!pickupDate || !timeSlot) {
      toast({
        title: "Missing information",
        description: "Please select date and time",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      // Map time slot to canonical code
      const timeSlotMap: Record<string, string> = {
        '9:00 AM - 11:00 AM': '9-11am',
        '12:00 PM - 2:00 PM': '12-2pm',
        '3:00 PM - 5:00 PM': '3-5pm',
        '6:00 PM - 8:00 PM': '6-8pm',
      };

      const { data: shipment, error: shipmentError } = await supabase
        .from('shipments')
        .insert({
          user_id: user.id,
          box_ids: createdBoxIds,
          box_count: createdBoxIds.length,
          type: 'to_storage',
          pickup_date: pickupDate.toISOString().split('T')[0],
          pickup_time_slot: timeSlotMap[timeSlot] || '9-11am',
          status: 'scheduled',
          is_first_pickup: true,
          onboarding_shipment: true
        })
        .select()
        .single();

      if (shipmentError) throw shipmentError;

      // Boxes stay as 'home' until physically picked up

      const billingStartDate = new Date(pickupDate);
      billingStartDate.setDate(billingStartDate.getDate() + 2);

      await supabase.from('profiles').update({
        onboarding_status: 'complete',
        billing_start_date: billingStartDate.toISOString().split('T')[0]
      }).eq('id', user.id);

      const sessionId = searchParams.get("session_id");
      if (sessionId) {
        await supabase.from('payment_sessions')
          .update({ 
            onboarding_completed: true,
            completed_at: new Date().toISOString()
          })
          .eq('stripe_session_id', sessionId);
      }

      toast({ title: "Success!", description: "Pickup scheduled" });
      navigate(`/checkout/success?onboarding=complete&session_id=${sessionId}`);
    } catch (error) {
      console.error('Error scheduling pickup:', error);
      toast({
        title: "Error",
        description: "Failed to schedule pickup",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const progressValue = 
    currentStep === 'create_boxes' ? 33 :
    currentStep === 'add_details' ? 66 : 100;

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <header className="bg-card border-b">
        <div className="container mx-auto px-4 py-4">
          <Package className="h-6 w-6 text-primary inline mr-2" />
          <span className="text-xl font-bold">Setup Your Storage</span>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <div className="mb-8">
          <Progress value={progressValue} className="mb-2" />
          <div className="flex justify-between text-sm text-muted-foreground">
            <span className={currentStep === 'create_boxes' ? 'text-primary font-semibold' : ''}>
              1. Create Boxes
            </span>
            <span className={currentStep === 'add_details' ? 'text-primary font-semibold' : ''}>
              2. Add Details
            </span>
            <span className={currentStep === 'schedule_pickup' ? 'text-primary font-semibold' : ''}>
              3. Schedule Pickup
            </span>
          </div>
        </div>

        {currentStep === 'create_boxes' && (
          <Card>
            <CardHeader>
              <CardTitle>Name Your {boxSlots} Boxes</CardTitle>
              <p className="text-muted-foreground">
                Give each box a name to help you remember what's inside
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={quickFillBoxNames}
                className="mb-4"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Quick Fill (Box 1, Box 2, ...)
              </Button>

              {Array.from({ length: boxSlots }).map((_, index) => (
                <div key={index} className="flex gap-3">
                  <div className="flex-1">
                    <Input
                      placeholder={`e.g., Winter Clothes, Holiday Decor`}
                      value={boxNames[index]}
                      onChange={(e) => {
                        const newNames = [...boxNames];
                        newNames[index] = e.target.value;
                        setBoxNames(newNames);
                      }}
                    />
                  </div>
                  <Select
                    value={boxCategories[index]}
                    onValueChange={(value) => {
                      const newCategories = [...boxCategories];
                      newCategories[index] = value;
                      setBoxCategories(newCategories);
                    }}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Clothing">Clothing</SelectItem>
                      <SelectItem value="Holiday">Holiday</SelectItem>
                      <SelectItem value="Books">Books</SelectItem>
                      <SelectItem value="Kitchen">Kitchen</SelectItem>
                      <SelectItem value="Sports">Sports</SelectItem>
                      <SelectItem value="Electronics">Electronics</SelectItem>
                      <SelectItem value="Toys">Toys</SelectItem>
                      <SelectItem value="Documents">Documents</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}

              <div className="pt-4">
                <Button 
                  onClick={handleCreateBoxes} 
                  disabled={loading}
                  className="w-full"
                >
                  Create {boxSlots} Boxes <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {currentStep === 'add_details' && (
          <Card>
            <CardHeader>
              <CardTitle>Add Photos & Items (Optional)</CardTitle>
              <p className="text-muted-foreground">
                You can skip this and add details later from your dashboard
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-center text-muted-foreground py-8">
                📸 Photo upload and item cataloging can be done from your dashboard anytime
              </p>

              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setCurrentStep('create_boxes')}
                  className="flex-1"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back
                </Button>
                <Button 
                  onClick={() => setCurrentStep('schedule_pickup')}
                  className="flex-1"
                >
                  Continue <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {currentStep === 'schedule_pickup' && (
          <Card>
            <CardHeader>
              <CardTitle>Schedule Your FREE First Pickup</CardTitle>
              <p className="text-muted-foreground">
                When should we pick up your {boxSlots} boxes?
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <label className="font-semibold block mb-2">Pickup Date</label>
                <Calendar
                  mode="single"
                  selected={pickupDate}
                  onSelect={setPickupDate}
                  disabled={(date) => 
                    date < new Date() || 
                    date.getDay() === 0 ||
                    date > new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)
                  }
                  className="rounded-md border"
                />
              </div>

              <div>
                <label className="font-semibold block mb-2">Time Window</label>
                <div className="grid grid-cols-2 gap-3">
                  {['9:00 AM - 11:00 AM', '12:00 PM - 2:00 PM', '3:00 PM - 5:00 PM', '6:00 PM - 8:00 PM'].map((slot) => (
                    <Button
                      key={slot}
                      variant={timeSlot === slot ? "default" : "outline"}
                      onClick={() => setTimeSlot(slot)}
                    >
                      {slot}
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <label className="font-semibold block mb-2">Special Instructions (Optional)</label>
                <Textarea
                  placeholder="e.g., Ring doorbell, boxes in lobby..."
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                />
              </div>

              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setCurrentStep('add_details')}
                  className="flex-1"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back
                </Button>
                <Button 
                  onClick={handleSchedulePickup}
                  disabled={loading || !pickupDate || !timeSlot}
                  className="flex-1"
                >
                  Confirm Pickup Schedule
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default GuidedStorageOnboarding;